// Source-level import resolver scanner for compile-time `.luis` inlining.
// It preserves comments/content and resolves only top-level declaration imports.
import { appendRawChar, appendResolvedImportSource, createResolvedImportSource } from './line-map'
import { isWhitespace } from './chars'
import { processTopLevelAtRule } from './handler'
import { createDirectiveOrderState, markContentSeen } from './order'
import { parseTopLevelAtRuleDeclarationAt } from './scan'
import type { ImportResolveContext } from './types'
import type { ResolvedImportSource } from './line-map-types'
import { registerSourceVariables } from './variables'

// Resolve one source document and inline `.luis` imports at declaration points.
export function resolveSourceRecursive(source: string, sourceId: string, context: ImportResolveContext): ResolvedImportSource {
  registerSourceVariables(source, sourceId, context.variableOwners, context.warnings)
  const orderState = createDirectiveOrderState()
  const result = createResolvedImportSource()
  let index = 0
  let line = 1
  let braceDepth = 0
  let quote: "'" | '"' | null = null
  let inLineComment = false
  let inBlockComment = false

  // Start one source scan that processes only top-level declaration at-rules.
  while (index < source.length) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Check newline boundaries to keep warning line numbers accurate.
    if (char === '\n') {
      line += 1
    }

    // Check whether the scanner is inside a line comment.
    if (inLineComment) {
      // Check whether the line comment ends at this newline.
      if (char === '\n') {
        inLineComment = false
      }
      appendRawChar(result, char, sourceId, line)
      index += 1
      continue
    }

    // Check whether the scanner is inside a block comment.
    if (inBlockComment) {
      // Check whether the block comment ends at `*/`.
      if (char === '*' && next === '/') {
        inBlockComment = false
        appendRawChar(result, '*', sourceId, line)
        appendRawChar(result, '/', sourceId, line)
        index += 2
        continue
      }
      appendRawChar(result, char, sourceId, line)
      index += 1
      continue
    }

    // Check whether the scanner is inside a quoted region.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      }
      appendRawChar(result, char, sourceId, line)
      index += 1
      continue
    }

    // Check whether the current character starts a quoted region.
    if (char === "'" || char === '"') {
      quote = char
      appendRawChar(result, char, sourceId, line)
      index += 1
      continue
    }

    // Check whether the current character starts a comment.
    if (char === '/' && next === '*') {
      inBlockComment = true
      appendRawChar(result, '/', sourceId, line)
      appendRawChar(result, '*', sourceId, line)
      index += 2
      continue
    } else if (char === '/' && next === '/') {
      inLineComment = true
      appendRawChar(result, '/', sourceId, line)
      appendRawChar(result, '/', sourceId, line)
      index += 2
      continue
    }

    // Check block depth so only top-level directives are processed.
    if (char === '{') {
      braceDepth += 1
      markContentSeen(orderState)
      appendRawChar(result, char, sourceId, line)
      index += 1
      continue
    } else if (char === '}') {
      braceDepth = Math.max(0, braceDepth - 1)
      appendRawChar(result, char, sourceId, line)
      index += 1
      continue
    }

    // Check top-level `@` positions for declaration at-rules.
    if (braceDepth === 0 && char === '@') {
      const atRule = parseTopLevelAtRuleDeclarationAt(source, index, line)
      // Check whether the current `@` token parsed into a declaration at-rule.
      if (!atRule) {
        markContentSeen(orderState)
        appendRawChar(result, char, sourceId, line)
        index += 1
        continue
      }

      const nextOutput = processTopLevelAtRule(
        atRule.statement,
        atRule.lineStart,
        sourceId,
        context,
        orderState,
        resolveSourceRecursive,
      )
      appendResolvedImportSource(result, nextOutput)
      index = atRule.end
      line = atRule.lineEnd
      continue
    }

    // Check top-level non-whitespace text to lock directive ordering.
    if (braceDepth === 0 && !isWhitespace(char)) {
      markContentSeen(orderState)
    }

    appendRawChar(result, char, sourceId, line)
    index += 1
  }

  // Return source with compile-time `.luis` imports resolved.
  return result
}
